<script lang="ts">
  import { onMount } from 'svelte';
  import { currentSubscription, enablePush, getPublicKey, pushSupported, testPushNotification } from '$lib/api/push';
  import { getErrorMessage } from '$lib/api/client';

  let supported = $state(false);
  let permission = $state<NotificationPermission | 'unavailable'>('unavailable');
  let configured = $state(false);
  let subscribed = $state(false);
  let busy = $state(false);
  let message = $state('');
  let error = $state('');

  async function refresh() {
    supported = pushSupported();
    permission = supported ? Notification.permission : 'unavailable';
    configured = Boolean(await getPublicKey());
    subscribed = Boolean(await currentSubscription());
  }

  async function enable() {
    busy = true;
    message = '';
    error = '';
    try {
      const result = await enablePush();
      if (!result.ok) {
        error = result.error ?? 'Push notification setup failed.';
      } else {
        message = 'Push notifications are enabled on this device.';
      }
      await refresh();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      busy = false;
    }
  }

  async function sendTest() {
    busy = true;
    message = '';
    error = '';
    try {
      // Re-register the browser subscription for the current signed-in user
      // before testing; a browser subscription can outlive an app session.
      const result = await enablePush();
      if (!result.ok) {
        error = result.error ?? 'Push notification setup failed.';
        await refresh();
        return;
      }
      await testPushNotification();
      message = 'Test notification sent. It should appear shortly.';
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      busy = false;
    }
  }

  onMount(() => {
    void refresh();
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => document.removeEventListener('visibilitychange', refreshWhenVisible);
  });
</script>

<div class="mx-auto max-w-2xl space-y-5 p-4 sm:p-6">
  <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <h1 class="text-xl font-semibold text-slate-900">Push notifications</h1>
    <p class="mt-1 text-sm text-slate-600">
      Fix punch-in notification access on this device and send a test notification.
    </p>

    <dl class="mt-5 divide-y divide-slate-100 rounded-xl border border-slate-100">
      <div class="flex items-center justify-between gap-4 px-4 py-3 text-sm">
        <dt class="text-slate-600">Browser support</dt>
        <dd class={supported ? 'font-medium text-emerald-700' : 'font-medium text-red-700'}>
          {supported ? 'Supported' : 'Not supported'}
        </dd>
      </div>
      <div class="flex items-center justify-between gap-4 px-4 py-3 text-sm">
        <dt class="text-slate-600">Permission</dt>
        <dd class={permission === 'granted' ? 'font-medium text-emerald-700' : 'font-medium text-amber-700'}>
          {permission === 'granted' ? 'Granted' : permission === 'denied' ? 'Blocked' : permission === 'default' ? 'Not decided' : 'Unavailable'}
        </dd>
      </div>
      <div class="flex items-center justify-between gap-4 px-4 py-3 text-sm">
        <dt class="text-slate-600">Browser subscription</dt>
        <dd class={subscribed ? 'font-medium text-emerald-700' : 'font-medium text-amber-700'}>
          {subscribed ? 'Registered' : 'Not registered'}
        </dd>
      </div>
    </dl>

    {#if permission === 'denied'}
      <div class="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        This site is blocked from showing the permission prompt. Use the browser’s site settings for this page, set Notifications to Allow, then return here and select <strong>Request permission again</strong>.
      </div>
    {:else if !configured}
      <div class="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
        Push notifications are not configured on the Teams server. This needs an administrator fix.
      </div>
    {/if}

    <div class="mt-5 flex flex-wrap gap-3">
      <button class="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50" disabled={busy || !supported || !configured} onclick={enable}>
        {permission === 'denied' ? 'Request permission again' : 'Enable push notifications'}
      </button>
      <button class="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50" disabled={busy || !subscribed} onclick={sendTest}>
        Send test notification
      </button>
    </div>

    {#if message}
      <p class="mt-4 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800" role="status">{message}</p>
    {/if}
    {#if error}
      <p class="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-800" role="alert">{error}</p>
    {/if}
  </section>
</div>
